/**
 * Adapter: turn a `viewRulesStore` `FullPreset` into a Compass
 * `LensDefinition`.
 *
 * The Compass renderer reads `attrs.lens.{color,badge,icon}` stamped
 * by `ExplorerViewService`. Instead of maintaining parallel
 * `LensDefinition` files for colorByField/heatmap (which duplicate the
 * shared rules engine), we resolve the active preset's first display
 * rule into a decorator on demand.
 *
 * Handled rule kinds:
 *   - `display.colorByField.field` → categorical palette, deterministic
 *     by value. Badge shows the value.
 *   - `display.heatmap.field` → linear interpolation between minColor
 *     and maxColor based on the min/max observed in the scene (auto-
 *     scaled when the rule leaves them unset).
 *
 * Returns `null` when the preset has no eligible display rule (filter-
 * only presets fall through to the default / icon lens path).
 */

import type {
  FullPreset,
  HeatmapOptions,
} from '../../../core/assistant/models/ViewRules';
import type { Node as SceneNode } from '../../../core/models/scene';
import type {
  LensDefinition,
  LensDecoration,
} from '../models/explorer/ExplorerLens';
import { resolveNodeField } from '../../../core/assistant/utils/conditionEvaluation';

/**
 * Categorical palette. Mirrors `CATEGORY_COLORS` in
 * `ViewRuleBuilder.tsx` so deck-wide colour choices match across the
 * panel UI and the Compass canvas.
 */
const CATEGORY_COLORS: readonly string[] = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

/**
 * Deterministic palette slot for a categorical value. We hash the
 * string so adding a new value does not reshuffle existing colors.
 */
function colorForCategoricalValue(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[idx];
}

function interpolateColor(minHex: string, maxHex: string, t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const [r1, g1, b1] = hexToRgb(minHex);
  const [r2, g2, b2] = hexToRgb(maxHex);
  const r = Math.round(r1 + (r2 - r1) * clamp);
  const g = Math.round(g1 + (g2 - g1) * clamp);
  const b = Math.round(b1 + (b2 - b1) * clamp);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const v = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

/**
 * Compute min/max of a numeric field across a node set. Used when a
 * heatmap rule leaves `minValue`/`maxValue` unset.
 */
export function heatmapScale(
  nodes: readonly SceneNode[],
  field: string,
): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const n of nodes) {
    const v = resolveNumericLensField(n, field);
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity || max === -Infinity) return null;
  return { min, max };
}

/**
 * Turn a preset into a LensDefinition. If the preset has no eligible
 * display rule, returns `null` so callers can fall through.
 *
 * `sceneNodes` supplies the set used for heatmap auto-scaling. When
 * omitted, heatmaps use the rule's explicit min/max or render
 * `dataMissing` when neither is available.
 */
export function presetAsLens(
  preset: FullPreset,
  sceneNodes?: readonly SceneNode[],
): LensDefinition | null {
  const displayRule = preset.rules.find(
    (r) => r.display?.colorByField || r.display?.heatmap,
  );
  if (!displayRule) return null;

  const colorBy = displayRule.display?.colorByField;
  const heatmap = displayRule.display?.heatmap;

  if (colorBy) {
    const field = colorBy.field;
    return {
      id: preset.id,
      label: preset.name,
      decorate(node: SceneNode): LensDecoration | null {
        const raw = resolveLensField(node, field);
        if (raw === null || raw === undefined || raw === '') {
          return { dataMissing: true, badge: { label: `no ${field}`, tone: 'neutral' } };
        }
        const value = String(raw);
        return {
          color: colorForCategoricalValue(value),
          badge: { label: value, tone: 'neutral' },
        };
      },
    };
  }

  if (heatmap) {
    const scale = resolveHeatmapScale(heatmap, sceneNodes);
    return {
      id: preset.id,
      label: preset.name,
      decorate(node: SceneNode): LensDecoration | null {
        const v = resolveNumericLensField(node, heatmap.field);
        if (!Number.isFinite(v)) {
          return { dataMissing: true, badge: { label: `no ${heatmap.field}`, tone: 'neutral' } };
        }
        if (!scale || scale.max === scale.min) {
          return { color: heatmap.minColor };
        }
        const t = (v - scale.min) / (scale.max - scale.min);
        return {
          color: interpolateColor(heatmap.minColor, heatmap.maxColor, t),
          badge: {
            label: formatNumeric(v, heatmap.field),
            tone: t > 0.66 ? 'critical' : t > 0.33 ? 'warn' : 'neutral',
          },
        };
      },
    };
  }

  return null;
}

function resolveHeatmapScale(
  heatmap: HeatmapOptions,
  sceneNodes?: readonly SceneNode[],
): { min: number; max: number } | null {
  if (heatmap.minValue !== undefined && heatmap.maxValue !== undefined) {
    return { min: heatmap.minValue, max: heatmap.maxValue };
  }
  if (!sceneNodes || sceneNodes.length === 0) return null;
  return heatmapScale(sceneNodes, heatmap.field);
}

function formatNumeric(v: number, field: string): string {
  if (field === 'cost') {
    return v < 10 ? `$${v.toFixed(2)}/mo` : `$${v.toFixed(0)}/mo`;
  }
  if (field === 'waste') {
    return v < 10 ? `$${v.toFixed(2)}/mo waste` : `$${v.toFixed(0)}/mo waste`;
  }
  if (field === 'utilization') return `${Math.round(v)}% util`;
  if (field === 'ageDays') return `${Math.round(v)}d`;
  return String(v);
}

type LensNode = { id: string; attrs?: Record<string, unknown>; props?: Record<string, unknown> };

function resolveLensField(node: SceneNode, field: string): unknown {
  const lensNode = node as LensNode;
  if (field === 'cost') return readMonthlyCostUsd(lensNode);
  if (field === 'utilization') return readUtilizationPct(lensNode);
  if (field === 'waste') return readWasteMonthlyUsd(lensNode);
  return resolveNodeField(lensNode, field);
}

function resolveNumericLensField(node: SceneNode, field: string): number {
  const raw = resolveLensField(node, field);
  return numericValue(raw);
}

function readMonthlyCostUsd(node: LensNode): number | null {
  const attrs = node.attrs ?? {};
  const props = node.props ?? {};
  const direct = firstNumeric(attrs.monthlyCostUsd, attrs.monthlySpend, props.monthlyCostUsd, props.monthlySpend);
  if (direct !== null) return direct;
  return costObjectValue(attrs.cost ?? props.cost);
}

function readUtilizationPct(node: LensNode): number | null {
  const attrs = node.attrs ?? {};
  const props = node.props ?? {};
  const explicit = firstNumeric(
    attrs.utilizationPct,
    attrs.utilization,
    attrs.cpuUtilizationPct,
    attrs.cpuUtilization,
    attrs.averageCpuUtilization,
    attrs.memoryUtilizationPct,
    props.utilizationPct,
    props.utilization,
    props.cpuUtilizationPct,
    props.cpuUtilization,
    props.averageCpuUtilization,
    props.memoryUtilizationPct,
  );
  if (explicit !== null) return normalisePct(explicit);
  const state = String(attrs.state ?? props.state ?? '').toLowerCase();
  if (state === 'stopped' || state === 'terminated' || state === 'idle') return 0;
  return null;
}

function readWasteMonthlyUsd(node: LensNode): number | null {
  const attrs = node.attrs ?? {};
  const props = node.props ?? {};
  const explicit = firstNumeric(attrs.wastedMonthlyUsd, attrs.wasteMonthlyUsd, attrs.waste, props.wastedMonthlyUsd, props.wasteMonthlyUsd, props.waste);
  if (explicit !== null) return explicit;
  const cost = readMonthlyCostUsd(node);
  if (cost === null) return null;
  const utilization = readUtilizationPct(node);
  if (utilization === null) return cost;
  return cost * (1 - Math.max(0, Math.min(100, utilization)) / 100);
}

function costObjectValue(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'object' || raw === null) return numericValue(raw);
  const record = raw as Record<string, unknown>;
  const currency = typeof record.currency === 'string' ? record.currency.toUpperCase() : 'USD';
  if (currency !== 'USD') return null;
  return firstNumeric(record.monthly, record.monthlyUsd, record.amount, record.value);
}

function firstNumeric(...values: unknown[]): number | null {
  for (const value of values) {
    const numeric = numericValue(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function numericValue(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[%,$]/g, '').trim());
    return Number.isFinite(parsed) ? parsed : NaN;
  }
  return NaN;
}

function normalisePct(value: number): number {
  return value > 0 && value <= 1 ? value * 100 : value;
}
